import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import { announce } from "@/lib/liveRegion";

/**
 * MilestoneMoment — the small celebratory bottom-sheet that fires the
 * first time a user hits a conservation checkpoint. Presentational only:
 * parent owns the queue via `useMilestones()` and shows the next one when
 * the user dismisses the current.
 *
 * Design notes:
 * - Enters with a bottom-slide + soft settle (respects `prefers-reduced-motion`).
 * - The card sits above the mobile bottom-nav so users can dismiss without
 *   accidentally tapping through to Camera / Gallery.
 * - Auto-dismisses after 6.5s if the user leaves it alone, but never
 *   without them seeing it. Timer pauses when the tab is hidden AND when
 *   the card is focused/hovered (keyboard + mouse dwell both count as
 *   "user is looking at it"). Escape dismisses immediately.
 * - Focus moves to the close button on entry so a keyboard-only user
 *   can dismiss without hunting through 40+ Tab stops.
 * - The screen-reader announcement is written into AppShell's persistent
 *   sr-only live region (see lib/liveRegion.js) — mounting a live region
 *   with its content in place is silently ignored by most SRs, so the
 *   dialog itself carries no aria-live and is safe to describe as role=dialog.
 */
export default function MilestoneMoment({ milestone, onDismiss }) {
  const { t } = useTranslation();
  const [entered, setEntered] = useState(false);
  const [dwelling, setDwelling] = useState(false);
  const closeBtnRef = useRef(null);

  // Slide-in on next paint so the CSS transition actually fires.
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const handleDismiss = useCallback(() => {
    setEntered(false);
    // Wait for the exit animation before removing from the DOM so React
    // doesn't unmount mid-transition (which flashes).
    setTimeout(() => onDismiss?.(), 220);
  }, [onDismiss]);

  // Move focus to the close button on entry — the card is mounted at the
  // very end of the DOM (bottom of body) so a keyboard-only user would
  // otherwise need dozens of Tab presses to reach it before it expires.
  useEffect(() => {
    if (!milestone) return;
    // Delay just past the slide-in so screen-reader focus events don't race
    // the entrance transition.
    const id = setTimeout(() => closeBtnRef.current?.focus?.(), 260);
    return () => clearTimeout(id);
  }, [milestone?.id]);

  // Announce the milestone to screen readers via AppShell's persistent
  // sr-only live region. The dialog itself does NOT carry aria-live —
  // announcing here means the SR reliably picks it up on the first mount.
  useEffect(() => {
    if (!milestone) return;
    const title = t(`milestones.catalog.${milestone.id}.title`, {
      defaultValue: milestone.title,
    });
    const message = t(`milestones.catalog.${milestone.id}.message`, {
      defaultValue: milestone.message,
    });
    announce(`${t("milestones.unlockedLabel")}: ${title}. ${message}`);
  }, [milestone?.id, t]);

  // Auto-dismiss timer — pauses while the tab is hidden or the user is
  // actively dwelling on the card (mouse or keyboard focus). Escape
  // dismisses immediately regardless of dwell.
  useEffect(() => {
    if (!milestone) return;
    if (dwelling) return; // dwelling === paused
    if (document.visibilityState !== "visible") return;

    const timer = setTimeout(handleDismiss, 6500);
    const onVis = () => {
      if (document.visibilityState !== "visible") clearTimeout(timer);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [milestone?.id, dwelling, handleDismiss]);

  // Escape-to-dismiss is always active while the card is mounted.
  useEffect(() => {
    if (!milestone) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleDismiss();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [milestone?.id, handleDismiss]);

  if (!milestone) return null;

  // Prefer i18n-keyed copy so translations work; fall back to the server
  // catalog for milestones the client hasn't been redeployed with yet.
  const title = t(`milestones.catalog.${milestone.id}.title`, {
    defaultValue: milestone.title,
  });
  const message = t(`milestones.catalog.${milestone.id}.message`, {
    defaultValue: milestone.message,
  });

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] pointer-events-none"
      data-testid="milestone-moment"
    >
      <div
        className={`mx-auto mb-4 max-w-md px-4 transition-all duration-500 ease-out ${
          entered ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
        }`}
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0) + 76px)" }}
        onMouseEnter={() => setDwelling(true)}
        onMouseLeave={() => setDwelling(false)}
        onFocus={() => setDwelling(true)}
        onBlur={() => setDwelling(false)}
      >
        <div
          className="pointer-events-auto relative overflow-hidden rounded-3xl border border-leaf/20 bg-white shadow-botanical"
          role="dialog"
          aria-labelledby={`milestone-title-${milestone.id}`}
          aria-describedby={`milestone-desc-${milestone.id}`}
        >
          {/* Soft leaf-tinted glow — botanical accent, low ink so it doesn't
              distract from the title. */}
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-br from-leaf/[0.08] via-transparent to-[#E9C46A]/10"
          />

          <button
            ref={closeBtnRef}
            type="button"
            onClick={handleDismiss}
            aria-label={t("common.close")}
            data-testid="milestone-close"
            className="absolute right-3 top-3 z-10 w-8 h-8 rounded-full bg-white/90 hover:bg-white text-leaf-mist hover:text-leaf-ink focus-visible:outline-2 focus-visible:outline-leaf transition flex items-center justify-center"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>

          <div className="relative flex gap-4 p-5">
            <div
              className="shrink-0 w-14 h-14 rounded-2xl bg-leaf/10 flex items-center justify-center text-3xl"
              aria-hidden
            >
              {milestone.emoji}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-[0.18em] text-leaf font-medium">
                {t("milestones.unlockedLabel")}
              </p>
              <h3
                id={`milestone-title-${milestone.id}`}
                className="mt-1 font-display text-xl text-leaf-ink leading-tight"
                data-testid="milestone-title"
              >
                {title}
              </h3>
              <p
                id={`milestone-desc-${milestone.id}`}
                className="mt-2 text-sm text-leaf-ink/85 leading-relaxed"
              >
                {message}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
